import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../common/components/Input";
import { IMedia } from "../../../../model/unityObject";
import ImageCard from "../../../gallery/components/ImageCard";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-quizz-image-multiple stores ONE field that is shaped differently in
// the two stores:
//   • base content object → list_answers: [{ text, imageId, is_right }]
//     (structural source of truth; imageId + is_right are shared across all
//     languages, .text holds the source-language text)
//   • per-language PLD     → list_answers: string[] (the .text projection only)
// This bespoke editor keeps the full object array in react-hook-form. NodeForm
// seeds it by merging the content object's structure with the active language's
// PLD text, and projects it back to a string[] for the PLD on save. The full
// object array round-trips on the content PUT (source language only).
//
// has_multiple_answers (base content, shared across languages) is a BEHAVIOUR
// switch, not a checkbox: when OFF the correctness selector is single-choice
// (radio — exactly one right answer); when ON it is multi-choice (checkbox).
// Mirrors content-quizz-3's QuizThreeAnswersField. NodeForm seeds it.
// See docs/plans/partial/node-form-quizz-json-editors.md §3.2.

export type ImageMultipleAnswer = {
  text: string;
  imageId: number | null;
  is_right: boolean;
};

// minItems from the slopify jsonSchema (listAnswersWithMin); soft max from the
// documented generation range ("Emit 3-4 options"). Existing data with more
// rows is preserved — we only stop the user from adding past the cap.
const MIN_ANSWERS = 3;
const MAX_ANSWERS = 4;

const NOMEDIA_FALLBACK: IMedia = {
  createdAt: "",
  id: -1,
  name: "",
  label: "",
  organization: 3,
  s3_url: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/NOMEDIA.png",
  type: null,
  updatedAt: "",
  filename: "NOMEDIA.png",
  tags: [],
  upload_status: "completed",
} as unknown as IMedia;

interface QuizImageMultipleAnswersFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** Full media library, to resolve each answer's imageId to a thumbnail. */
  mediasList: IMedia[];
  /** Opens the shared gallery modal targeting the given answer row. */
  onPickImage: (index: number) => void;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (add/remove/correctness/image) are source-language only. */
  isSourceLanguage: boolean;
  /** Optional per-answer character cap (none in the current schema). */
  maxLength?: number;
}

const QuizImageMultipleAnswersField = ({
  control,
  setValue,
  label,
  mediasList,
  onPickImage,
  disabled = false,
  isSourceLanguage,
  maxLength,
}: QuizImageMultipleAnswersFieldProps) => {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "list_answers" });
  const answers: ImageMultipleAnswer[] = Array.isArray(watched) ? watched : [];

  const watchedHasMultiple = useWatch({ control, name: "has_multiple_answers" });
  const hasMultiple = !!watchedHasMultiple;

  // Text is editable in any language; structure (correctness, images, count,
  // single-vs-multi mode) is locked outside the source language.
  const structureLocked = disabled || !isSourceLanguage;

  const write = (next: ImageMultipleAnswer[]) =>
    setValue("list_answers", next, { shouldDirty: true });

  const handleTextChange = (index: number, value: string) => {
    write(answers.map((a, i) => (i === index ? { ...a, text: value } : a)));
  };

  const handleToggleCorrect = (index: number) => {
    if (hasMultiple) {
      // Multi-select: flip just this row.
      write(answers.map((a, i) => (i === index ? { ...a, is_right: !a.is_right } : a)));
    } else {
      // Single-answer: exactly this row true, all others false.
      write(answers.map((a, i) => ({ ...a, is_right: i === index })));
    }
  };

  const handleToggleHasMultiple = () => {
    const next = !hasMultiple;
    setValue("has_multiple_answers", next, { shouldDirty: true });
    if (!next) {
      // Switching to single-answer: coerce to exactly one correct. Keep the
      // first currently-correct row; if none are correct, leave all false so
      // the author makes an explicit choice.
      const firstCorrect = answers.findIndex((a) => a.is_right);
      if (firstCorrect >= 0) {
        write(answers.map((a, i) => ({ ...a, is_right: i === firstCorrect })));
      }
    }
  };

  const handleAdd = () => {
    // New rows carry no image yet; the author must pick one (save is gated on
    // it in NodeForm). is_right defaults false so they pick the correct one(s).
    write([...answers, { text: "", imageId: null, is_right: false }]);
  };

  const handleRemove = (index: number) => {
    write(answers.filter((_, i) => i !== index));
  };

  const canAdd = !structureLocked && answers.length < MAX_ANSWERS;
  const canRemove = !structureLocked && answers.length > MIN_ANSWERS;

  // A quizz with no correct answer is unplayable; an answer with no image
  // would render broken in VR. Both are surfaced here and block the save in
  // NodeForm. Only meaningful in the source language, where structure is edited.
  const hasNoCorrectAnswer = isSourceLanguage && !answers.some((a) => a.is_right);
  const hasMissingImage = isSourceLanguage && answers.some((a) => !(Number(a.imageId) > 0));

  // At the min/max, the remove / add buttons disable. Surface WHY — a silently
  // greyed button reads as a bug. Shown only when nothing more pressing (a
  // blocking error) needs the hint line, and only where the action is possible
  // in principle (source language, edit mode).
  const atMinAnswers = answers.length <= MIN_ANSWERS;
  const atMaxAnswers = answers.length >= MAX_ANSWERS;
  const noBlockingError = !hasNoCorrectAnswer && !hasMissingImage;
  const showMinAnswersHint = !disabled && isSourceLanguage && atMinAnswers && noBlockingError;
  const showMaxAnswersHint =
    !disabled && isSourceLanguage && atMaxAnswers && !atMinAnswers && noBlockingError;
  const minAnswersHint = t("pages.project.editNode.quizzImageMinAnswersHint") as string;
  const maxAnswersHint = t("pages.project.editNode.quizzImageMaxAnswersHint") as string;

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <Tooltip
          message={!canAdd && !structureLocked ? maxAnswersHint : ""}
          className="shrink-0"
          classNameChildren="right-0"
        >
          <button
            type="button"
            onClick={handleAdd}
            disabled={!canAdd}
            className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            <span>{t("pages.project.editNode.quizzAddAnswer")}</span>
          </button>
        </Tooltip>
      </div>

      <label className="flex items-center gap-2 mb-3 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={hasMultiple}
          onChange={handleToggleHasMultiple}
          disabled={structureLocked}
          className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
        />
        {t("pages.project.editNode.quizzHasMultipleAnswers")}
      </label>

      {!isSourceLanguage && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      )}

      {/* Single reserved hint line (avoids layout shift). Priority: blocking
          errors (red) first, then the informational min/max-answers note (grey)
          explaining why the remove / add buttons are disabled. */}
      <p
        className={`min-h-[1.25rem] mb-2 text-xs font-medium ${
          showMinAnswersHint || showMaxAnswersHint ? "text-gray-500 italic" : "text-red-600"
        }`}
      >
        {!disabled && hasNoCorrectAnswer
          ? t("pages.project.editNode.quizzNeedsCorrectAnswer")
          : !disabled && hasMissingImage
          ? t("pages.project.editNode.quizzImageNeedsImage")
          : showMinAnswersHint
          ? minAnswersHint
          : showMaxAnswersHint
          ? maxAnswersHint
          : ""}
      </p>

      <div className="space-y-3">
        {answers.map((answer, index) => {
          const media =
            mediasList.find((m) => Number(m.id) === Number(answer.imageId)) ?? NOMEDIA_FALLBACK;
          return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-3 flex items-start gap-3"
            >
              <div className="w-40 shrink-0">
                <ImageCard
                  media={media}
                  currentMedia={Number(answer.imageId) > 0 ? Number(answer.imageId) : undefined}
                  isMediaPickerMode={false}
                  showThreeDotsMenu={false}
                  disabled={structureLocked}
                  handleButtonClick={() => onPickImage(index)}
                  handleCardClick={() => {
                    if (!structureLocked) onPickImage(index);
                  }}
                  className="w-40"
                />
              </div>

              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <label
                  className="flex items-center gap-2"
                  title={t("pages.project.editNode.quizzMarkCorrect")}
                >
                  <input
                    type={hasMultiple ? "checkbox" : "radio"}
                    name="quizz-image-multiple-correct"
                    checked={!!answer.is_right}
                    onChange={() => handleToggleCorrect(index)}
                    disabled={structureLocked}
                    className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
                  />
                  <span className="text-xs text-gray-500">
                    {t("pages.project.editNode.quizzMarkCorrect")}
                  </span>
                </label>

                <Input
                  type="text"
                  value={answer.text ?? ""}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  placeholder={t("pages.project.editNode.quizzAnswerPlaceholder") as string}
                  className="dynamic-field"
                />
              </div>

              <Tooltip
                message={!canRemove && !structureLocked ? minAnswersHint : ""}
                className="shrink-0"
                classNameChildren="right-0"
              >
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  disabled={!canRemove}
                  className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={t("general.delete") as string}
                >
                  <MdDeleteOutline size={20} />
                </button>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizImageMultipleAnswersField;
